import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { PLAN_LIST, PA_PLAN_LIST } from "@/components/pricingData";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isPersonal = params.get("product") === "personal";
  const plan = params.get("plan");
  const planName = [...PLAN_LIST, ...PA_PLAN_LIST].find((p) => p.id === plan)?.name;

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const account_type = isPersonal ? "personal" : "business";
      const chosenPlan = plan || (isPersonal ? "pa_essential" : undefined);
      await register({ ...form, plan: chosenPlan, account_type });
      if (isPersonal) navigate(`/pa/onboarding${plan ? `?plan=${plan}` : ""}`);
      else navigate(`/onboarding${plan ? `?plan=${plan}` : ""}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-1.5 justify-center mb-8" data-testid="auth-logo">
          <img src="/operamind-mark.png" alt="OperaMind" className="h-11 w-11 object-contain" />
          <div className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-xl tracking-tight text-slate-900">Opera<span className="text-blue-600">Mind</span></span>
            <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-900">Intelligence. Orchestrated.</span>
          </div>
        </Link>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-bold tracking-tight">{isPersonal ? "Create your Personal Assistant account" : "Create your account"}</h1>
          <p className="text-slate-500 text-sm mt-1">
            {planName ? <>You selected the <span className="font-semibold text-blue-600">{planName}</span> plan. Start your 3-day free trial.</> : (isPersonal ? "Your AI personal assistant, ready in minutes." : "Get started with OperaMind.")}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1" placeholder="Jane Smith" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1" placeholder="you@business.com" />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="mt-1" placeholder="At least 6 characters" />
            </div>
            {err && <p data-testid="register-error" className="text-sm text-red-600">{err}</p>}
            <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="h-4 w-4" /></>}
            </Button>
            <p className="text-center text-xs text-slate-400 leading-relaxed" data-testid="register-legal-consent">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="text-slate-500 underline hover:text-slate-700">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-slate-500 underline hover:text-slate-700">Privacy Policy</Link>.
            </p>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account? <Link to="/login" className="text-blue-600 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
